import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';

import { Icon } from '@iconify/react';
import CloseIcon from '@mui/icons-material/Close';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import axiosInstance from 'src/api/axiosInstance';

const token = sessionStorage.getItem('token');

const downloadHelpDocument = async () => {
  try {
    const { data } = await axiosInstance.get('/rest/help/download', {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (data.error) {
      console.error(data.error);
      return;
    }

    window.open(data.downloadUrl, '_blank', 'noopener');
  } catch (error) {
    console.error('Failed to download help document:', error);
  }
};

export default function HelpDialog({ open, onClose, onDownload }) {
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      const fetchProvider = async () => {
        setLoading(true);
        try {
          const userToken = sessionStorage.getItem('token');
          const response = await axiosInstance.get('/rest/preferences/email-provider-name', {
            headers: { Authorization: `Bearer ${userToken}` },
          });
          setProvider(response.data);
        } catch (error) {
          console.error('Failed to fetch email provider name:', error);
          setProvider('OTHER');
        } finally {
          setLoading(false);
        }
      };

      fetchProvider();
    } else {
      setProvider(null);
      setLoading(false);
    }
  }, [open]);

  const handleEmailSupportClick = () => {
    if (loading) return;

    const email = 'support@merudesk.com';
    const subject = 'Support Request';
    const encodedSubject = encodeURIComponent(subject);
    let url = '';

    const providerUpper = provider ? provider.toUpperCase() : '';

    if (providerUpper === 'GOOGLE') {
      url = `https://mail.google.com/mail/?view=cm&fs=1&to=${email}&su=${encodedSubject}`;
    } else if (providerUpper === 'ZOHO') {
      url = `https://mail.zoho.com/zm/#compose?to=${email}&subject=${encodedSubject}`;
    } else {
      // MICROSOFT, OTHER, null, etc.
      url = `https://outlook.office.com/mail/deeplink/compose?to=${email}&subject=${encodedSubject}`;
    }

    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          overflow: 'hidden',
          boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
          border: '1px solid rgba(255,255,255,0.12)',
        },
      }}
      BackdropProps={{
        sx: {
          backdropFilter: 'blur(4px)',
          backgroundColor: 'rgba(0,0,0,0.35)',
        },
      }}
    >
      {/* ── Header Banner ── */}
      <Box
        sx={{
          position: 'relative',
          background: 'linear-gradient(135deg, #0a5c37 0%, #0E9F60 60%, #2dd4a0 100%)',
          pt: 4,
          pb: 3.5,
          px: 3,
          textAlign: 'center',
          overflow: 'hidden',
        }}
      >
        {/* Decorative circles */}
        <Box
          sx={{
            position: 'absolute',
            top: -28,
            right: -28,
            width: 110,
            height: 110,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.07)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -20,
            left: -20,
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.06)',
          }}
        />

        {/* Close button */}
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            color: 'rgba(255,255,255,0.85)',
            bgcolor: 'rgba(255,255,255,0.12)',
            backdropFilter: 'blur(4px)',
            width: 30,
            height: 30,
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.22)',
              color: '#fff',
            },
          }}
        >
          <CloseIcon sx={{ fontSize: '1rem' }} />
        </IconButton>

        {/* Icon */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 62,
            height: 62,
            borderRadius: '18px',
            background: 'rgba(255,255,255,0.15)',
            backdropFilter: 'blur(8px)',
            border: '1.5px solid rgba(255,255,255,0.25)',
            mb: 1.5,
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-headset-icon lucide-headset"><path d="M3 11h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-5Zm0 0a9 9 0 1 1 18 0m0 0v5a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3Z" /><path d="M21 16v2a4 4 0 0 1-4 4h-5" /></svg>
        </Box>

        <Typography
          sx={{
            fontWeight: 600,
            fontSize: '1rem',
            color: '#fff',
          }}
        >
          Help & Support
        </Typography>

        <Typography
          sx={{
            color: 'rgba(255,255,255,0.75)',
            fontSize: '0.8rem',
          }}
        >
          We’re here to help you
        </Typography>
      </Box>

      {/* ── Body ── */}
      <DialogContent sx={{ px: 3, py: 3, bgcolor: '#FAFFFE' }}>
        <Typography
          sx={{
            textAlign: 'center',
            color: '#4B5563',
            fontSize: '0.875rem',
            mb: 2.5,
          }}
        >
          Have a question or need assistance? <br /> Reach out to our support team.
        </Typography>

        {/* Email Card */}
        <Box
          onClick={handleEmailSupportClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1.75,
            borderRadius: '14px',
            background: 'linear-gradient(135deg, #F0FDF6, #DCFCE7)',
            border: '1.5px solid #86EFAC',
            textDecoration: 'none',
            transition: 'all 0.25s ease',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.7 : 1,
            '&:hover': {
              transform: loading ? 'none' : 'translateY(-1px)',
            },
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #0E9F60, #0a5c37)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <EmailRoundedIcon sx={{ color: '#fff' }} />
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '0.7rem', color: '#6B7280' }}>Email Support</Typography>
            <Typography sx={{ fontWeight: 700, color: '#0a5c37' }}>support@merudesk.com</Typography>
          </Box>

          {loading ? (
            <CircularProgress size={20} sx={{ color: '#0E9F60' }} />
          ) : (
            <OpenInNewRoundedIcon sx={{ color: '#0E9F60' }} />
          )}
        </Box>

        {/* Document Support */}
        <Box
          onClick={downloadHelpDocument}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1.75,
            borderRadius: '14px',
            background: 'linear-gradient(135deg, #EFF6FF, #DBEAFE)',
            border: '1.5px solid #93C5FD',
            cursor: 'pointer',
            transition: 'all 0.25s ease',
            mt: 2,
            '&:hover': {
              transform: 'translateY(-1px)',
            },
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #2563EB, #1E40AF)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon icon="basil:document-solid" width="24" height="24" style={{ color: '#fff' }} />
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '0.7rem', color: '#6B7280' }}>User Manual</Typography>
            <Typography sx={{ fontWeight: 700, color: '#1E40AF' }}>Refer Documentation</Typography>
          </Box>

          <OpenInNewRoundedIcon sx={{ color: '#2563EB' }} />
        </Box>

        <Typography
          sx={{
            textAlign: 'center',
            mt: 2,
            fontSize: '0.75rem',
            color: '#9CA3AF',
          }}
        >
          Typical response time: within one business day.
        </Typography>
      </DialogContent>
    </Dialog>
  );
}

HelpDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onDownload: PropTypes.func,
};
